/*
 * Talks to the server. Every call sends the company being worked in (X-Company-Id) and the session cookie. Errors come back
 * as ApiError with the server's code and its plain-words message, ready to show.
 *
 * In the preview build the same calls go to a made-up server in the browser (src/demo/server.ts) instead of the network.
 */
import { previewPicture, previewServer } from "../demo/server";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Anything else the error answer carried (the bulk change's confirm token). */
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export type Transport = (
  method: string,
  path: string,
  headers: Record<string, string>,
  body: unknown,
) => Promise<{ status: number; body: unknown }>;

const network: Transport = async (method, path, headers, body) => {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { ...headers, ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  return { status: res.status, body: text ? (JSON.parse(text) as unknown) : null };
};

const transport: Transport = __PREVIEW__ ? previewServer : network;

const COMPANY_KEY = "steward.company";

export function savedCompanyId(): string | null {
  try {
    return localStorage.getItem(COMPANY_KEY);
  } catch {
    return null;
  }
}

export function saveCompanyId(id: string | null): void {
  try {
    if (id) localStorage.setItem(COMPANY_KEY, id);
    else localStorage.removeItem(COMPANY_KEY);
  } catch {
    // Private windows can refuse storage; the company is then picked again next time.
  }
}

let companyId: string | null = savedCompanyId();
const listeners = new Set<() => void>();
export function setCompany(id: string | null): void {
  if (id === companyId) return;
  companyId = id;
  saveCompanyId(id);
  for (const l of listeners) l();
}

/** The company being worked in, for React (useSyncExternalStore). */
export const companyStore = {
  get: () => companyId,
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export async function api<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
  extra: Record<string, string> = {},
): Promise<T> {
  const headers: Record<string, string> = { ...extra };
  if (companyId) headers["x-company-id"] = companyId;
  // The server wants a JSON body on every POST, even an empty one.
  const send = body ?? (method === "POST" ? {} : undefined);
  let res: { status: number; body: unknown };
  try {
    res = await transport(method, `/v1/${path}`, headers, send);
  } catch {
    throw new ApiError(0, "offline", "Could not reach the server. Check your connection and try again.");
  }
  if (res.status >= 400) {
    const { code, message, ...extra } = (res.body ?? {}) as { code?: string; message?: string } & Record<string, unknown>;
    throw new ApiError(res.status, code ?? "error", message ?? "That didn't work. Try again.", extra);
  }
  return res.body as T;
}

/**
 * A picture from the server (a profile picture) as a link an <img> can show, since a plain link can not send the company
 * header. Null when it is gone.
 */
export async function pictureUrl(path: string): Promise<string | null> {
  if (__PREVIEW__) return previewPicture(path);
  const res = await fetch(`/v1/${path}`, {
    credentials: "same-origin",
    headers: companyId ? { "x-company-id": companyId } : {},
  });
  return res.ok ? URL.createObjectURL(await res.blob()) : null;
}

/** A public link to a quote section's picture or file (no sign in: the quote's token is the key). */
export function quoteFileSrc(token: string, fileId: string): string {
  const path = `public/quote/${token}/files/${fileId}`;
  return __PREVIEW__ ? (previewPicture(path) ?? "") : `/v1/${path}`;
}

/**
 * Downloads a file from the server (the year-end zip) with the company header, which a plain link can not send. Answers
 * false in the preview, where there is no server to build the file.
 */
export async function downloadFile(path: string, filename: string): Promise<boolean> {
  if (__PREVIEW__) return false;
  const res = await fetch(`/v1/${path}`, {
    credentials: "same-origin",
    headers: companyId ? { "x-company-id": companyId } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new ApiError(res.status, body.code ?? "error", body.message ?? "That didn't work. Try again.");
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
